import { ReactNode, useMemo } from 'react';
import { PlotEditorContextProvider, PlotUpdateHandler } from './PlotEditorContext';
import { ChartCardV1 } from '../../../../../../packages/db-schema/shared/chartcards/types';

export interface PlotEditorRootProps {
  plotlyJson: any;
  onUpdate: PlotUpdateHandler;
  dataColumns?: string[];
  chartType?: string;
  chartCard?: ChartCardV1 | null;
  planSpec?: any;
  children: ReactNode;
}

/**
 * Root component for the plot editor.
 * Provides context and wraps all editor tabs.
 */
export function PlotEditorRoot({
  plotlyJson,
  onUpdate,
  dataColumns,
  chartType = 'scatter', // Default fallback
  chartCard,
  planSpec,
  children,
}: PlotEditorRootProps) {
  // Derive chartType from plotSpec if available
  const derivedChartType = useMemo(() => {
    // Try to get from plotSpec first
    const plotSpecFromJson = (plotlyJson as any)?._plotSpec || planSpec;
    if (plotSpecFromJson?.chart_family) {
      return plotSpecFromJson.chart_family;
    }
    
    // Fallback: infer from trace types
    const traces = Array.isArray(plotlyJson?.data) ? plotlyJson.data : [];
    if (traces.some((t: any) => t.type === 'bar')) return 'bar';
    if (traces.some((t: any) => t.type === 'histogram')) return 'histogram';
    if (traces.some((t: any) => t.type === 'scatter' || !t.type)) {
      const hasLines = traces.some((t: any) => t.mode?.includes('lines'));
      return hasLines ? 'line' : 'scatter';
    }
    
    return chartType;
  }, [plotlyJson, planSpec, chartType]);

  // Extract chartCard and planSpec from plotlyJson if not provided
  const extractedChartCard = useMemo(() => {
    if (chartCard) return chartCard;
    // Try to extract from plotlyJson metadata
    const meta = plotlyJson?.meta || {};
    if (meta.chartcard_json && meta.chartcard_json.schema_version === 'ChartCard.v1') {
      return meta.chartcard_json as ChartCardV1;
    }
    return null;
  }, [chartCard, plotlyJson]);

  const extractedPlanSpec = useMemo(() => {
    if (planSpec) return planSpec;
    // Try to extract from plotlyJson
    return (plotlyJson as any)?._plotSpec || null;
  }, [planSpec, plotlyJson]);

  return (
    <PlotEditorContextProvider
      plotlyJson={plotlyJson}
      dataColumns={dataColumns}
      chartType={derivedChartType}
      chartCard={extractedChartCard}
      planSpec={extractedPlanSpec}
      onUpdate={onUpdate}
    >
      {children}
    </PlotEditorContextProvider>
  );
}

